import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Search, Music, Loader2, Play, Pause } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'

const CATEGORY_LABELS: Record<string, string> = {
  chill: 'Chill',
  classical: 'Classical',
  comedy: 'Comedy',
  country: 'Country',
  electronic: 'Electronic',
  'epic-dramatic': 'Epic Dramatic',
  fantasy: 'Fantasy',
  horror: 'Horror',
  jazz: 'Jazz',
  rock: 'Rock',
  'romantic-sentimental': 'Romantic Sentimental',
  underscoring: 'Underscoring',
  uplifting: 'Uplifting',
  world: 'World',
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

export default function AdminMusicListPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const tracks = useQuery(api.musicLibrary.adminListAllTracks, isAdmin ? {} : 'skip')
  const toggleTrack = useMutation(api.musicLibrary.adminToggleTrack)

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [showFilter, setShowFilter] = useState<'all' | 'enabled' | 'disabled'>('all')
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const rafRef = useRef<number>(0)
  const scrubbingRef = useRef(false)

  const updateTime = useCallback(() => {
    if (audioRef.current && !scrubbingRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
    rafRef.current = requestAnimationFrame(updateTime)
  }, [])

  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const filtered = useMemo(() => {
    if (!tracks) return []
    let result = tracks

    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q) ||
          (CATEGORY_LABELS[t.category] || '').toLowerCase().includes(q)
      )
    }

    if (categoryFilter) {
      result = result.filter((t) => t.category === categoryFilter)
    }

    if (showFilter === 'enabled') {
      result = result.filter((t) => t.enabled)
    } else if (showFilter === 'disabled') {
      result = result.filter((t) => !t.enabled)
    }

    return result
  }, [tracks, search, categoryFilter, showFilter])

  const categories = useMemo(() => {
    if (!tracks) return []
    const counts = new Map<string, { total: number; enabled: number }>()
    for (const t of tracks) {
      const c = counts.get(t.category) || { total: 0, enabled: 0 }
      c.total++
      if (t.enabled) c.enabled++
      counts.set(t.category, c)
    }
    return Array.from(counts.entries())
      .map(([cat, c]) => ({ category: cat, label: CATEGORY_LABELS[cat] || cat, ...c }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [tracks])

  const stats = useMemo(() => {
    if (!tracks) return { total: 0, enabled: 0, disabled: 0 }
    const enabled = tracks.filter((t) => t.enabled).length
    return { total: tracks.length, enabled, disabled: tracks.length - enabled }
  }, [tracks])

  const handleToggle = useCallback(
    async (trackId: string, currentEnabled: boolean) => {
      await toggleTrack({ trackId, enabled: !currentEnabled })
    },
    [toggleTrack]
  )

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    cancelAnimationFrame(rafRef.current)
    setPlayingId(null)
    setCurrentTime(0)
    setDuration(0)
  }, [])

  const handlePlay = useCallback(
    (trackId: string, url: string) => {
      if (playingId === trackId) {
        stopAudio()
        return
      }
      stopAudio()
      const audio = new Audio(url)
      audio.volume = 0.5
      audio.onended = () => stopAudio()
      audio.onloadedmetadata = () => setDuration(audio.duration)
      audio.play()
      audioRef.current = audio
      setPlayingId(trackId)
      setCurrentTime(0)
      rafRef.current = requestAnimationFrame(updateTime)
    },
    [playingId, stopAudio, updateTime]
  )

  const handleScrubStart = useCallback(() => {
    scrubbingRef.current = true
  }, [])

  const handleScrub = useCallback((value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value
      setCurrentTime(value)
    }
  }, [])

  const handleScrubEnd = useCallback(() => {
    scrubbingRef.current = false
  }, [])

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-sm text-neutral-500">Admin access required.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Music Library</h1>
        <p className="text-sm text-neutral-500 mt-1">
          {stats.total} tracks &middot; {stats.enabled} enabled &middot; {stats.disabled} disabled
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search tracks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100"
          />
        </div>

        <select
          value={categoryFilter || ''}
          onChange={(e) => setCategoryFilter(e.target.value || null)}
          className="text-sm px-3 py-2 rounded-lg border border-neutral-200 bg-white text-neutral-700 outline-none focus:border-neutral-400"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.category} value={c.category}>
              {c.label} ({c.enabled}/{c.total})
            </option>
          ))}
        </select>

        <div className="flex items-center rounded-lg border border-neutral-200 bg-white overflow-hidden">
          {(['all', 'enabled', 'disabled'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setShowFilter(f)}
              className={cn(
                'px-3 py-2 text-sm font-medium capitalize transition-colors',
                showFilter === f
                  ? 'bg-neutral-900 text-white'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {!tracks ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200 text-xs font-medium text-neutral-500 uppercase tracking-wider">
                <th className="text-left px-4 py-3 w-12">On</th>
                <th className="text-left px-4 py-3 w-10"></th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3 w-44">Category</th>
                <th className="text-left px-4 py-3 w-48">Track ID</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((track) => {
                const isPlaying = playingId === track.id
                return (
                  <tr
                    key={track.id}
                    className={cn(
                      'border-b border-neutral-100 transition-colors',
                      !track.enabled && 'bg-neutral-50/50 opacity-60'
                    )}
                  >
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => handleToggle(track.id, track.enabled)}
                        className={cn(
                          'relative w-9 h-5 rounded-full transition-colors',
                          track.enabled ? 'bg-emerald-500' : 'bg-neutral-300'
                        )}
                      >
                        <span
                          className={cn(
                            'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform',
                            track.enabled && 'translate-x-4'
                          )}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => handlePlay(track.id, track.url)}
                        className={cn(
                          'p-1 rounded-md transition-colors',
                          isPlaying
                            ? 'bg-neutral-900 text-white'
                            : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700'
                        )}
                      >
                        {isPlaying ? (
                          <Pause className="h-3.5 w-3.5" />
                        ) : (
                          <Play className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-neutral-900">{track.title}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] font-mono text-neutral-400 w-8 text-right tabular-nums">
                            {isPlaying ? formatTime(currentTime) : '0:00'}
                          </span>
                          <input
                            type="range"
                            min={0}
                            max={isPlaying ? duration : 100}
                            step={0.1}
                            value={isPlaying ? currentTime : 0}
                            onPointerDown={handleScrubStart}
                            onPointerUp={handleScrubEnd}
                            onChange={(e) => handleScrub(parseFloat(e.target.value))}
                            className="flex-1 h-2 appearance-none bg-neutral-200 rounded-full cursor-pointer accent-neutral-900 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-neutral-900"
                          />
                          <span className="text-[10px] font-mono text-neutral-400 w-8 tabular-nums">
                            {isPlaying && duration > 0 ? formatTime(duration) : '--:--'}
                          </span>
                        </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                        {CATEGORY_LABELS[track.category] || track.category}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-neutral-400">{track.id}</td>
                  </tr>
                )
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-neutral-400">
                    <Music className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No tracks match your filters</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
